import { Skeleton } from "@/components/ui/skeleton";
import { app, db } from "@/firebase/firebase";
import { useAuth } from "@/hook/useAuth";
import { sendCustomGaEvent } from "@/scripts/ga";
import useToastNotification from "@/hook/useToastNotification";
import { updateInterestedCountries } from "@/store/feature/country/countryApi";
import { pastQualification } from "../common/Helper";
import {
  bookTheSlot,
  getAvailableSlot,
} from "@/store/feature/talkToExpert/talkToExpert";
import { getUserMeeting, sentOtp, verifyOtp } from "@/store/feature/user/userApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { CustomerInfo, Slots } from "@/store/types/talkToExpertTypes";
import { getDateSlots } from "@/utils/getDateSlots";
import { doc, getDoc } from "firebase/firestore";
import Image from "next/image";
import {
  CalendarDays,
  ChevronLeft,
  ChevronDown,
  Clock3,
  Mail,
  Phone,
  Video,
  X,
  User,
  ArrowRight,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CommonAuth from "../auth/CommonAuth";
import ChooseCountryDropdown from "../common/ChooseCountryDropdown";
import EmailVerification from "../common/EmailVerificationPopup";
import { BookConfirmCheck } from "../common/Icons";
import RedirectToDashboard from "../common/RedirectToDashboard";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { createCustomTokenWithUserId } from "@/app/actions/loginUserWithOTP";
import { registerUserWithOTP } from "@/app/actions/registerUserWithOTP";
import { getStoredTrackingParams } from "@/utils/utmTracker";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { getAuth, signInWithCustomToken } from "firebase/auth";

interface Option {
  id: number | string;
  title: string;
}

const Loader = () => (
  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
);

const FullScreenLoader = ({ content }: { content: string }) => (
  <div className="fixed left-0 top-0 z-[9999] flex h-screen w-full flex-col items-center justify-center bg-white">
    <Image
      className="w-full h-auto max-w-[300px] xl:max-w-[400px]"
      src="/assets/gif/booking.gif"
      alt="logo"
      width={200}
      height={100}
      unoptimized
    />
    <p className="text-center text-base font-semibold text-gray-700 sm:text-lg md:text-xl">
      {content}
    </p>
  </div>
);

interface TalkToExportsProps {
  expertForm: boolean;
  isPopup?: boolean;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setExpertForm: React.Dispatch<React.SetStateAction<boolean>>;
  customClass?: string;
  initialSessionType?: "telephonic" | "video";
  initialCountry?: string;
  initialIntake?: string;
  initialQualification?: string;
}

interface UserDetails {
  email: string;
  role: "student" | string;
  firstName: string;
  middleName: string;
  lastName: string;
  phoneNumber: string;
  primary_phone_number?: string;
  isVerified: boolean;
  hasNoLastName: boolean;
  userId: string;
  user_id?: string;
  createdAt?: { seconds: number; nanoseconds: number };
  updatedAt?: { seconds: number; nanoseconds: number };
  [key: string]: any;
}

interface SlotItem {
  time: string; // display label e.g. "10:30 AM"
  startUtc: string; // original UTC string from API, used for booking payload
  isUnavailable?: boolean;
}

// Skeleton component for slot buttons
const SlotButtonSkeleton = () => (
  <Skeleton className="h-[36px] sm:h-[40px] md:h-[44px] rounded-[42px] bg-gray-200" />
);

// Skeleton grid for slots
const SlotsSkeleton = () => (
  <div className="py-2 grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
    {Array.from({ length: 8 }).map((_, index) => (
      <SlotButtonSkeleton key={index} />
    ))}
  </div>
);

const SLOT_TIME_ZONE = "Asia/Kolkata";

const slots = getDateSlots();
const nonSundaySlots = slots.filter(({ fullDate }) => {
  const date = new Date(`${fullDate}T00:00:00`);
  return !Number.isNaN(date.getTime()) && date.getDay() !== 0;
});

const currentYear = new Date().getFullYear();
const intakeListOptions = [
  { id: 1, title: `${currentYear}` },
  { id: 2, title: `${currentYear + 1}` },
  { id: 3, title: `${currentYear + 2}` },
];

const extractErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  const err = error as any;
  if (err?.response?.data?.message) {
    const msg = String(err.response.data.message);
    if (!msg.toLowerCase().includes("request failed with status code")) return msg;
  }
  if (err?.data?.message) {
    const msg = String(err.data.message);
    if (!msg.toLowerCase().includes("request failed with status code")) return msg;
  }
  if (err?.message && typeof err.message === "string") {
    if (!err.message.toLowerCase().includes("request failed with status code")) return err.message;
  }
  return null;
};

const TalkToExperts = ({
  expertForm,
  customClass,
  setExpertForm,
  setCurrentStep,
  currentStep,
  isPopup = true,
  initialSessionType = "telephonic",
  initialCountry,
  initialIntake,
  initialQualification,
}: TalkToExportsProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const fullPathName = usePathname();
  const apiCallRef = useRef<string>("");
  const isInitialMount = useRef(true);

  const [sessionType, setSessionType] = useState<"telephonic" | "video">(
    initialSessionType,
  );
  const [selectedIntake, setSelectedIntake] = useState<string>(
    initialIntake || `${currentYear}`,
  );
  const [showAlreadyBookedPopUp, setShowAlreadyBookedPopUp] = useState(false);
  const { countryList } = useAppSelector((state) => state.country);
  const { meetings = [] } = useAppSelector((state) => state.userProfile);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isConfirmingSlot, setIsConfirmingSlot] = useState(false);
  const [isFetchingSlots, setIsFetchingSlots] = useState(false);
  const { user, emailVerified, loading: authLoading } = useAuth();
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    initialCountry ? [initialCountry.toLowerCase()] : [],
  );
  const [selectedQualification, setSelectedQualification] = useState(
    initialQualification || "",
  );
  const [countryError, setCountryError] = useState("");
  const [qualificationError, setQualificationError] = useState("");
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [showAnonymousAuth, setShowAnonymousAuth] = useState(false);
  const [showIntroScreen, setShowIntroScreen] = useState(true);
  const [isChangingStep, setIsChangingStep] = useState(false);
  const [isFetchingUserDetails, setIsFetchingUserDetails] = useState(false);
  const [wizardSubStep, setWizardSubStep] = useState<1 | 2 | 3>(1);

  // Guest Contact & OTP Verification Form State
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const [fullNameError, setFullNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (initialSessionType) setSessionType(initialSessionType);
  }, [initialSessionType]);

  useEffect(() => {
    if (initialCountry && selectedCountries.length === 0) {
      setSelectedCountries([initialCountry.toLowerCase()]);
    }
  }, [initialCountry, selectedCountries.length]);

  useEffect(() => {
    if (initialIntake) setSelectedIntake(initialIntake);
  }, [initialIntake]);

  useEffect(() => {
    if (initialQualification) setSelectedQualification(initialQualification);
  }, [initialQualification]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!user?.uid || user.isAnonymous) {
        setUserDetails(null);
        setIsFetchingUserDetails(false);
        return;
      }

      setIsFetchingUserDetails(true);

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserDetails(userSnap.data() as UserDetails);
        } else {
          setUserDetails(null);
          console.warn("User document not found");
        }
      } catch (error) {
        setUserDetails(null);
        console.error("Error fetching user details:", error);
      } finally {
        setIsFetchingUserDetails(false);
      }
    };
    fetchUserDetails();
  }, [user]);

  // Pre-fill user contact info if logged in
  useEffect(() => {
    if (user && !user.isAnonymous) {
      const parts = [userDetails?.firstName, userDetails?.middleName, userDetails?.lastName]
        .map((p) => (p ? String(p).trim() : ""))
        .filter((p) => p !== "" && p.toLowerCase() !== "null");
      const derivedName = parts.join(" ").trim();
      const rawDisplayName = user.displayName ? String(user.displayName).replace(/null/gi, "").trim() : "";
      const resolvedName = derivedName || rawDisplayName;

      const rawEmail = userDetails?.email || user.email || "";
      const resolvedEmail = rawEmail.toLowerCase().includes("null") ? "" : rawEmail;

      const rawPhone =
        userDetails?.phoneNumber ||
        userDetails?.primary_phone_number ||
        user.phoneNumber ||
        "";
      const resolvedPhone = rawPhone.toLowerCase().includes("null") ? "" : rawPhone;

      if (resolvedName) setFullName(resolvedName);
      if (resolvedEmail) setEmail(resolvedEmail);
      if (resolvedPhone) setPhone(resolvedPhone);
    }
  }, [user, userDetails]);

  const { availableSlots, counsellorId } = useAppSelector(
    (state) => state.talkToExpert,
  );

  const [selectedDate, setSelectedDate] = useState(
    nonSundaySlots[0]?.fullDate || slots[0]?.fullDate || "",
  );
  const [selectedSlotTime, setSelectedSlotTime] = useState<string>("");
  const [availableSlotList, setAvailableSlotList] = useState<SlotItem[]>([]);

  const { showToast } = useToastNotification();

  const startCountdown = (duration = 60) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setResendCountdown(duration);
    timerRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const closeSideBarHandler = () => {
    void sendCustomGaEvent("talk_to_experts_close", { step: currentStep });
    setExpertForm(false);
    setCurrentStep(1);
    setWizardSubStep(1);
    setShowAnonymousAuth(false);
    setShowIntroScreen(true);
    setIsChangingStep(false);
    setSelectedSlotTime("");
    setAvailableSlotList([]);
    setSelectedDate(nonSundaySlots[0]?.fullDate || slots[0]?.fullDate || "");
    setIsConfirmingSlot(false);
    setIsOtpSent(false);
    setOtp("");
    setOtpError("");
    setCountryError("");
    setQualificationError("");
    setSelectedQualification(initialQualification || "");
    apiCallRef.current = "";
    isInitialMount.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    const fallbackDate = nonSundaySlots[0]?.fullDate || slots[0]?.fullDate;
    if (fallbackDate && selectedDate !== fallbackDate) {
      dispatch(getAvailableSlot({ date: fallbackDate, sessionType }));
    }
    document.body.classList.remove("overflow-hidden");
  };

  useEffect(() => {
    if (!isPopup) return;
    if (expertForm) {
      document.body.classList.add("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [expertForm, isPopup]);

  useEffect(() => {
    if (!expertForm) {
      setShowAnonymousAuth(false);
      setShowIntroScreen(true);
      setIsChangingStep(false);
    }
  }, [expertForm]);

  useEffect(() => {
    if (currentStep !== 1) {
      setShowAnonymousAuth(false);
    }
  }, [currentStep]);

  const fetchSlots = useCallback(
    async (date: string, type: "telephonic" | "video" = sessionType, force = false) => {
      const cacheKey = `${date}_${type}`;
      if (!force && apiCallRef.current === cacheKey) return;
      apiCallRef.current = cacheKey;
      setIsFetchingSlots(true);
      try {
        await dispatch(getAvailableSlot({ date, sessionType: type }));
      } catch (error) {
        console.error("Error fetching slots:", error);
        apiCallRef.current = "";
      } finally {
        setIsFetchingSlots(false);
        setIsChangingStep(false);
      }
    },
    [dispatch, sessionType],
  );

  useEffect(() => {
    if (!isChangingStep) return;

    const fallbackTimer = window.setTimeout(() => {
      setIsChangingStep(false);
    }, 5000);

    return () => window.clearTimeout(fallbackTimer);
  }, [isChangingStep]);

  const handleSlotSelection = (slotTime: string) => {
    void sendCustomGaEvent("talk_to_experts_slot_select", {
      date: selectedDate,
      slot_time: slotTime,
      session_type: sessionType,
    });
    setSelectedSlotTime(slotTime);
  };

  const handleDateSelection = useCallback(
    (fullDate: string) => {
      if (fullDate !== selectedDate) {
        void sendCustomGaEvent("talk_to_experts_date_select", {
          date: fullDate,
          session_type: sessionType,
        });
        setSelectedDate(fullDate);
        setSelectedSlotTime("");
        setAvailableSlotList([]);
        fetchSlots(fullDate, sessionType);
      }
    },
    [selectedDate, sessionType, fetchSlots],
  );

  const handleSessionTypeChange = (newType: "telephonic" | "video") => {
    if (newType === sessionType) return;
    setSessionType(newType);
    setSelectedSlotTime("");
    setAvailableSlotList([]);
    fetchSlots(selectedDate, newType, true);
  };

  // Initial fetch when step 2 opens
  useEffect(() => {
    if (expertForm && isInitialMount.current && currentStep === 2) {
      fetchSlots(selectedDate, sessionType, true);
      isInitialMount.current = false;
    }
  }, [expertForm, currentStep, selectedDate, sessionType, fetchSlots]);

  useEffect(() => {
    const dateFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: SLOT_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: SLOT_TIME_ZONE,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const durationMinutes = sessionType === "telephonic" ? 15 : 30;
    const currentTime = Date.now();
    const todayInKolkata = dateFormatter.format(new Date());

    const bookedMeetingRanges = meetings
      .filter((meeting) => {
        const status = meeting.meetingStatus?.toLowerCase() || "";
        const meetingEnd = new Date(
          meeting.meetingMetadata?.end?.dateTime || "",
        ).getTime();
        if (!Number.isFinite(meetingEnd) || meetingEnd <= Date.now())
          return false;
        return ![
          "client_canceled",
          "counsellor_canceled",
          "canceled",
          "cancelled",
          "missed",
        ].includes(status);
      })
      .map((meeting) => ({
        start: new Date(meeting.meetingMetadata.start.dateTime).getTime(),
        end: new Date(meeting.meetingMetadata.end.dateTime).getTime(),
      }));

    const rawList: Slots[] = Array.isArray(availableSlots)
      ? availableSlots
      : (availableSlots as { list?: Slots[] })?.list || [];

    const slotsForSelectedDate = rawList
      .filter(({ start }) => {
        const slotDateStr = dateFormatter.format(new Date(start));
        return slotDateStr === selectedDate;
      })
      .map(({ start }) => ({
        time: timeFormatter.format(new Date(start)),
        startUtc: start,
      }));

    const processed: SlotItem[] = rawList
      .filter(({ start, end }) => {
        const slotMs = new Date(start).getTime();
        const slotEndMs = end ? new Date(end).getTime() : slotMs + durationMinutes * 60 * 1000;
        const slotDateStr = dateFormatter.format(new Date(start));
        if (slotDateStr !== selectedDate) return false;

        if (selectedDate === todayInKolkata && slotMs <= currentTime) {
          return false;
        }

        const clashes = bookedMeetingRanges.some(
          (m) => slotMs < m.end && slotEndMs > m.start,
        );
        if (clashes) return false;

        return true;
      })
      .map(({ start }) => {
        return { time: timeFormatter.format(new Date(start)), startUtc: start };
      });

    const selectedSlotFromDate = slotsForSelectedDate.find(
      (slot) => slot.time === selectedSlotTime,
    );
    const shouldKeepSelectedSlotVisible =
      Boolean(selectedSlotTime) &&
      Boolean(selectedSlotFromDate) &&
      !processed.some((slot) => slot.time === selectedSlotTime);

    const slotListToDisplay = shouldKeepSelectedSlotVisible
      ? [
        ...processed,
        {
          ...selectedSlotFromDate!,
          isUnavailable: true,
        },
      ].sort(
        (a, b) =>
          new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime(),
      )
      : processed;

    setAvailableSlotList(slotListToDisplay);

    setSelectedSlotTime((currentSelectedSlotTime) => {
      const selectedSlotStillAvailable = processed.some(
        (slot) => slot.time === currentSelectedSlotTime,
      );

      if (selectedSlotStillAvailable) {
        return currentSelectedSlotTime;
      }

      const selectedSlotStillVisible = slotsForSelectedDate.some(
        (slot) => slot.time === currentSelectedSlotTime,
      );

      if (selectedSlotStillVisible) {
        return currentSelectedSlotTime;
      }

      return "";
    });
  }, [availableSlots, meetings, selectedDate, selectedSlotTime, sessionType]);

  const displayName = useMemo(() => {
    const parts = [userDetails?.firstName, userDetails?.middleName, userDetails?.lastName]
      .map((p) => (p ? String(p).trim() : ""))
      .filter((p) => p !== "" && p.toLowerCase() !== "null");
    const derivedName = parts.join(" ").trim();
    const rawDisplayName = user?.displayName ? String(user.displayName).replace(/null/gi, "").trim() : "";
    return derivedName || rawDisplayName || fullName.trim() || userDetails?.email || user?.email || "Student";
  }, [userDetails, user, fullName]);

  const handleConfirmSlot = async (providedDetails?: {
    name?: string;
    email?: string;
    phone?: string;
    studentId?: string;
  }) => {
    if (isConfirmingSlot) return;
    void sendCustomGaEvent("talk_to_experts_confirm_click", {
      date: selectedDate,
      slot_time: selectedSlotTime,
      session_type: sessionType,
      selected_countries: selectedCountries.length,
    });

    const storedUserId = typeof window !== "undefined" ? localStorage.getItem("userId") : null;
    const studentIdToPass =
      providedDetails?.studentId ||
      userDetails?.user_id ||
      userDetails?.userId ||
      user?.uid ||
      storedUserId ||
      undefined;

    setIsConfirmingSlot(true);

    try {
      if (selectedCountries.length === 0) {
        setCountryError("Please select interested country.");
        return;
      }
      setCountryError("");

      if (!selectedQualification) {
        setQualificationError("Please select previous qualification.");
        return;
      }
      setQualificationError("");

      if (!selectedSlotTime || !selectedDate) {
        showToast("Invalid date or time selected.", "error");
        return;
      }

      const selectedSlot = availableSlotList.find(
        (s) => s.time === selectedSlotTime,
      );
      if (!selectedSlot) {
        showToast("Selected slot not found or unavailable.", "error");
        return;
      }

      const durationMinutes = sessionType === "telephonic" ? 15 : 30;
      const startTime = new Date(selectedSlot.startUtc);
      const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

      const nameToPass =
        providedDetails?.name ||
        displayName ||
        fullName.trim() ||
        "Student";

      const emailToPass =
        providedDetails?.email ||
        email.trim() ||
        userDetails?.email ||
        user?.email ||
        "student@edvia.com";

      const phoneToPass =
        providedDetails?.phone ||
        phone.trim() ||
        userDetails?.phoneNumber ||
        userDetails?.primary_phone_number ||
        user?.phoneNumber ||
        "+919999999999";

      const customerInfo: CustomerInfo = {
        name: nameToPass,
        email: emailToPass,
        phone: phoneToPass,
        sessionType,
        studentId: studentIdToPass,
        counsellorId: counsellorId || undefined,
      };

      const res = await dispatch(
        bookTheSlot({ startTime, endTime, customerInfo }),
      );
      const bookingResult = res.payload as { status?: boolean | number } | undefined;

      if (
        bookingResult &&
        typeof bookingResult === "object" &&
        "status" in bookingResult &&
        bookingResult.status === 409
      ) {
        setShowAlreadyBookedPopUp(true);
        showToast(
          "Session already booked. Please complete your current session from the dashboard before booking a new one.",
          "warning",
        );
      } else if (res.meta.requestStatus === "fulfilled" || bookingResult?.status === true) {
        showToast("Slot booked successfully!", "success");
        setCurrentStep(3); // Confirmation step
      } else {
        const friendlyMsg =
          extractErrorMessage(res.payload) ||
          "Failed to book slot. Please try another slot.";
        showToast(friendlyMsg, "error");
      }
    } catch (error: any) {
      console.error("Slot booking error:", error);
      const friendlyMsg =
        extractErrorMessage(error) ||
        error?.message ||
        "Failed to book slot! Please try again.";
      showToast(friendlyMsg, "error");
    } finally {
      setIsConfirmingSlot(false);
      const targetId = studentIdToPass || user?.uid;
      if (targetId) {
        dispatch(
          updateInterestedCountries({
            id: targetId,
            payload: {
              interestedCountries: selectedCountries,
              pastQualification: selectedQualification,
              preferredIntake: selectedIntake,
            },
          }),
        );
        dispatch(getUserMeeting(targetId));
      }
    }
  };

  // Step 2 Action -> If logged in, book slot directly; if guest, open contact & OTP step
  const handleProceedFromSubStep2 = async () => {
    if (!canConfirmSlot) return;

    if (user && !user.isAnonymous) {
      await handleConfirmSlot();
    } else {
      setWizardSubStep(3);
    }
  };

  const validateGuestDetails = () => {
    let isValid = true;

    if (!fullName.trim()) {
      setFullNameError("Please enter your full name");
      isValid = false;
    } else {
      setFullNameError("");
    }

    if (!email.trim()) {
      setEmailError("Please enter your email");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError("Please enter a valid email address");
      isValid = false;
    } else {
      setEmailError("");
    }

    if (!phone || !isValidPhoneNumber(phone)) {
      setPhoneError("Please enter a valid mobile number");
      isValid = false;
    } else {
      setPhoneError("");
    }

    return isValid;
  };

  // Send OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateGuestDetails()) return;

    setIsSendingOtp(true);
    setOtpError("");

    try {
      const response = await dispatch(sentOtp({ phoneNumber: phone })).unwrap();
      if (response?.user_id) {
        localStorage.setItem("userId", response.user_id);
        setUserAlreadyExists(true);
      } else {
        localStorage.removeItem("userId");
        setUserAlreadyExists(false);
      }

      showToast("OTP sent successfully to your mobile number!", "success");
      startCountdown(60);
      setIsOtpSent(true);
    } catch (error: any) {
      console.error("Error sending OTP:", error);
      showToast(error?.message || "Failed to send OTP. Please try again.", "error");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendCountdown > 0 || isSendingOtp) return;
    setIsSendingOtp(true);
    setOtpError("");
    try {
      const response = await dispatch(sentOtp({ phoneNumber: phone })).unwrap();
      if (response?.user_id) {
        localStorage.setItem("userId", response.user_id);
        setUserAlreadyExists(true);
      }
      showToast("New OTP sent successfully!", "success");
      setOtp("");
      startCountdown(60);
    } catch (error: any) {
      setOtpError(error?.message || "Failed to resend OTP");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify OTP and complete booking
  const handleVerifyOtpAndConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) {
      setOtpError("Please enter the complete 6-digit OTP");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError("");

    try {
      await dispatch(verifyOtp({ phoneNumber: phone, otp })).unwrap();

      localStorage.setItem(
        "otpVerified",
        JSON.stringify({ message: "success", time: new Date() }),
      );

      let resolvedUserId: string | undefined;

      try {
        const storedUserId = localStorage.getItem("userId");

        if (userAlreadyExists || storedUserId) {
          const tokenRes = await createCustomTokenWithUserId({
            uid: storedUserId || "",
          });
          if (tokenRes.success && tokenRes.customToken) {
            const firebaseAuth = getAuth(app);
            const userCred = await signInWithCustomToken(firebaseAuth, tokenRes.customToken);
            resolvedUserId = userCred.user?.uid || storedUserId || undefined;
            if (storedUserId) {
              localStorage.setItem("userDetails", JSON.stringify(storedUserId));
            }
          }
        } else {
          const nameParts = fullName.trim().split(/\s+/);
          const firstName = nameParts[0] || "Student";
          const lastName = nameParts.length >= 2 ? nameParts[nameParts.length - 1] : null;
          const middleName = nameParts.length > 2 ? nameParts[1] : null;
          const trackingParams = getStoredTrackingParams();

          const regRes = await registerUserWithOTP({
            firstName,
            middleName,
            lastName,
            email: email.trim() || null,
            phone,
            ...trackingParams,
            source: trackingParams.utm_campaign || trackingParams.source || "talk_to_experts_booking",
          });

          if (regRes.success && regRes.customToken) {
            const firebaseAuth = getAuth(app);
            const userCred = await signInWithCustomToken(firebaseAuth, regRes.customToken);
            resolvedUserId = userCred.user?.uid || regRes.userDetails?.userId;
            if (regRes.userDetails?.userId) {
              localStorage.setItem("userId", regRes.userDetails.userId);
              localStorage.setItem("userDetails", JSON.stringify(regRes.userDetails.userId));
            }
          }
        }
      } catch (authErr) {
        console.warn("Background user registration/auth warning:", authErr);
      }

      showToast("Verified! Finalizing your booking...", "success");

      await handleConfirmSlot({
        name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        studentId: resolvedUserId,
      });
    } catch (error: any) {
      console.error("OTP verification error:", error);
      const friendlyMsg = extractErrorMessage(error) || "Invalid or expired OTP. Please check and try again.";
      setOtpError(friendlyMsg);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const countryOptions = useMemo(
    () =>
      countryList?.map((country) => ({
        id: Number(country.country_id),
        title: country.country_name,
      })) || [],
    [countryList],
  );

  const qualificationOptions = useMemo(
    () =>
      pastQualification.map((item) => ({
        value: item.title,
        label: item.title,
      })),
    [],
  );

  const formattedSelectedDate = useMemo(() => {
    if (!selectedDate) return "";
    const parsedDate = new Date(`${selectedDate}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return selectedDate;
    return parsedDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [selectedDate]);

  const canConfirmSlot =
    selectedCountries.length > 0 &&
    Boolean(selectedSlotTime) &&
    Boolean(selectedQualification) &&
    !isConfirmingSlot;

  const isCheckingEmailVerification = false;
  const shouldBlockUnverifiedEmail = false;
  const shouldShowIntroScreen =
    fullPathName !== "/talk-to-expert" &&
    fullPathName !== "/tools/course-finder" &&
    currentStep !== 3 &&
    showIntroScreen &&
    !showAnonymousAuth;

  const handleCountryChange = useCallback((selected: Option[]) => {
    const converted = selected.map((opt) => opt.title.toLowerCase());
    setSelectedCountries(converted);
    if (converted.length > 0) setCountryError("");
  }, []);

  const handleQualificationChange = useCallback((selected: Option[]) => {
    const qualification = selected[0]?.title || "";
    setSelectedQualification(qualification);
    if (qualification) setQualificationError("");
  }, []);

  useEffect(() => {
    if (showAnonymousAuth && user && !user.isAnonymous) {
      setIsChangingStep(true);
      setShowAnonymousAuth(false);
      setCurrentStep(2);
    }
  }, [user, showAnonymousAuth, setCurrentStep]);

  const handleBookYourSlot = () => {
    if (authLoading) return;

    setShowIntroScreen(false);
    setShowAnonymousAuth(false);
    setIsChangingStep(true);
    setCurrentStep(2);
    setWizardSubStep(1);
  };

  const features = [
    {
      icon: Phone,
      title: "Expert Guidance",
      description: "Tailored advice for your goals",
    },
    {
      icon: CalendarDays,
      title: "Flexible Scheduling",
      description: "Choose a time that works for you",
    },
  ];

  const bookingDetails = [
    {
      label: "Session Type",
      value:
        sessionType === "telephonic"
          ? "Phone Call (15 Min)"
          : "Google Meet (30 Min)",
    },
    { label: "Name", value: displayName },
    {
      label: "Countries",
      value:
        selectedCountries.length > 0
          ? selectedCountries.map((c) => c.toUpperCase()).join(", ")
          : "Not specified",
    },
    { label: "Target Intake", value: selectedIntake },
    { label: "Qualification", value: selectedQualification },
    { label: "Date", value: formattedSelectedDate },
    { label: "Time", value: selectedSlotTime },
  ];

  return (
    <>
      {(authLoading || isChangingStep || isCheckingEmailVerification) &&
        fullPathName === "/talk-to-expert" ? (
        <FullScreenLoader
          content={
            authLoading
              ? "Checking your login status..."
              : isCheckingEmailVerification
                ? "Checking email verification..."
                : "Preparing booking form..."
          }
        />
      ) : (
        <>
          {showAlreadyBookedPopUp && <RedirectToDashboard />}
          {isPopup && expertForm && (
            <div
              className="fixed inset-0 z-[110] bg-slate-950/40 backdrop-blur-sm"
              onClick={closeSideBarHandler}
            ></div>
          )}
          <div
            className={
              isPopup
                ? "fixed inset-0 z-[120] flex items-end justify-center p-0 sm:items-center sm:p-4 md:p-6"
                : `w-full flex justify-center ${customClass ?? ""}`
            }
          >
            <div
              className={`w-full ${isPopup ? "h-[100dvh] max-w-[760px] sm:h-auto" : "max-w-[860px]"
                }`}
            >
              <div
                className={`relative overflow-hidden bg-white shadow-[0_25px_80px_rgba(15,23,42,0.18)] ${isPopup
                    ? "h-full rounded-t-[28px] sm:h-auto sm:rounded-[28px]"
                    : "rounded-[28px]"
                  }`}
              >
                <div className="flex flex-col ff_poppins">
                  {isPopup && (
                    <button
                      type="button"
                      onClick={closeSideBarHandler}
                      className="absolute right-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-[#4A5565] backdrop-blur-sm transition hover:text-black sm:right-2 sm:top-2 sm:h-8 sm:w-8 sm:bg-transparent sm:backdrop-blur-0"
                      aria-label="Close talk to experts modal"
                    >
                      <X size={19} />
                    </button>
                  )}

                  {/* ── INTRO SCREEN ── */}
                  {shouldShowIntroScreen ? (
                    <div className="grid min-h-[100dvh] grid-cols-1 bg-white bg-[linear-gradient(135deg,rgba(143,62,188,0.1)_0%,rgba(124,58,237,0.1)_50%,rgba(23,118,240,0.2)_100%)] sm:min-h-[434px] sm:grid-cols-[1.08fr_0.92fr]">
                      <div className="flex flex-col justify-between gap-6 px-5 pb-6 pt-14 sm:gap-8 sm:p-8 lg:p-10">
                        <div>
                          <span className="inline-flex rounded-full bg-[#1776F0] px-3 py-1 text-[10px] font-extrabold tracking-[0.22em] text-white">
                            ASK AN EXPERT
                          </span>
                          <h2 className="mt-5 max-w-[360px] text-[32px] font-semibold leading-[1.15] text-black sm:text-4xl">
                            Unlock the{" "}
                            <span className="text-[#9810FA]">
                              Insider Secrets
                            </span>{" "}
                            to Your Dream University
                          </h2>
                          <p className="mt-3 text-sm text-[#4A5565]">
                            Book a Free 1:1 Counseling call with an Expert
                          </p>
                          <div className="mt-6 space-y-4">
                            {features.map(
                              ({ icon: Icon, title, description }) => (
                                <div
                                  key={title}
                                  className="flex items-start gap-3"
                                >
                                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1776F0] text-white">
                                    <Icon size={17} />
                                  </span>
                                  <div>
                                    <p className="text-sm font-semibold text-black">
                                      {title}
                                    </p>
                                    <p className="text-xs text-[#6A7282]">
                                      {description}
                                    </p>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleBookYourSlot}
                          className="w-full cursor-pointer rounded-2xl bg-[#101828] px-4 py-4 text-base font-semibold text-white transition hover:bg-[#1776F0] sm:text-lg"
                        >
                          Book Your Slot
                        </button>
                      </div>
                      <div className="relative flex items-center justify-center px-5 pb-5 sm:p-6">
                        <div className="relative h-[240px] w-full overflow-hidden rounded-[28px] sm:h-[380px] sm:rounded-[32px]">
                          <Image
                            src="/assets/images/png/talkToExpert.png"
                            alt="Expert consultation"
                            fill
                            priority
                            className="object-cover object-center"
                            sizes="(min-width: 640px) 42vw, 100vw"
                          />
                        </div>
                      </div>
                    </div>
                  ) : /* ── STEP 1: AUTH ── */
                    currentStep === 1 ? (
                      <div className="max-h-[100dvh] overflow-y-auto bg-white px-5 pb-6 pt-14 sm:max-h-[90vh] sm:p-8">
                        {fullPathName !== "/talk-to-expert" && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowAnonymousAuth(false);
                              setShowIntroScreen(true);
                            }}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#4A5565] transition hover:text-black"
                          >
                            <ChevronLeft size={16} />
                            Back
                          </button>
                        )}
                        <div className="mx-auto mt-4 max-w-[520px]">
                          <CommonAuth />
                        </div>
                      </div>
                    ) : /* ── STEP 2: WIZARD FLOW ── */
                      currentStep === 2 ? (
                        <div className="relative max-h-[100dvh] overflow-hidden bg-[linear-gradient(135deg,#FDF2F8_0%,#FFF7ED_50%,#FFFFFF_100%)] sm:max-h-[90vh]">
                          {shouldBlockUnverifiedEmail && (
                            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 p-4 backdrop-blur-[6px] sm:p-6">
                              <div className="flex w-full max-w-[360px] flex-col items-center rounded-[24px] border border-white/80 bg-white/95 px-5 py-6 text-center shadow-[0_30px_80px_rgba(15,23,42,0.18)] sm:rounded-[28px] sm:px-8 sm:py-7">
                                <Image
                                  className="w-full max-w-[180px] sm:max-w-[220px]"
                                  width={300}
                                  height={400}
                                  alt="email-verify"
                                  src={"/assets/images/svg/emailVerify.svg"}
                                />
                                <p className="mt-4 text-center text-lg font-semibold text-black sm:text-[28px] sm:leading-[1.2]">
                                  Ready to chat with our experts?
                                </p>
                                <p className="ff_poppins mt-3 text-center text-sm font-medium text-[#4A5565] sm:text-base">
                                  Verify your email to unlock your expert session.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setShowVerifyEmail(true)}
                                  className="ff_poppins mt-5 cursor-pointer text-sm font-semibold text-[#5E57EB] underline underline-offset-4 transition hover:text-[#4B45C6] sm:text-base"
                                >
                                  Verify Now
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="max-h-[100dvh] overflow-y-auto px-5 pb-6 pt-5 sm:pt-7 md:pt-9 lg:pt-11 xl:pt-14 sm:max-h-[90vh] sm:p-8">
                            <div>
                              {/* Step Progress Bar */}
                              <div className="mb-6 flex items-center justify-between border-b border-slate-200/80 pb-4">
                                <div>
                                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#1776F0]">
                                    Step {wizardSubStep} of {user && !user.isAnonymous ? 2 : 3}
                                  </span>
                                  <h2 className="text-xl font-bold text-black sm:text-2xl mt-0.5">
                                    {wizardSubStep === 1
                                      ? "Consultation & Preferences"
                                      : wizardSubStep === 2
                                        ? "Select Date & Time"
                                        : "Enter Contact Details"}
                                  </h2>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span
                                    className={`h-2.5 w-8 rounded-full transition-all ${
                                      wizardSubStep === 1
                                        ? "bg-[#1776F0]"
                                        : "bg-emerald-500"
                                    }`}
                                  />
                                  <span
                                    className={`h-2.5 w-8 rounded-full transition-all ${
                                      wizardSubStep === 2
                                        ? "bg-[#1776F0]"
                                        : wizardSubStep > 2
                                          ? "bg-emerald-500"
                                          : "bg-slate-300"
                                    }`}
                                  />
                                  {(!user || user.isAnonymous) && (
                                    <span
                                      className={`h-2.5 w-8 rounded-full transition-all ${
                                        wizardSubStep === 3
                                          ? "bg-[#1776F0]"
                                          : "bg-slate-300"
                                      }`}
                                    />
                                  )}
                                </div>
                              </div>

                              {/* ──── WIZARD SUB-STEP 1: MODE & PROFILE PREFERENCES ──── */}
                              {wizardSubStep === 1 ? (
                                <div className="space-y-5">
                                  {/* Session Type Switcher */}
                                  <div>
                                    <label className="mb-2 block text-xs sm:text-sm font-semibold text-black">
                                      Select Session Mode <span className="text-red-500">*</span>
                                    </label>
                                    <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                                      <button
                                        type="button"
                                        onClick={() => handleSessionTypeChange("telephonic")}
                                        className={`flex items-center justify-center gap-2 rounded-xl border p-3.5 text-xs sm:text-sm font-semibold transition cursor-pointer ${
                                          sessionType === "telephonic"
                                            ? "border-[#1776F0] bg-[#EFF6FF] text-[#1776F0] shadow-sm ring-2 ring-[#1776F0]/20"
                                            : "border-[#E2E8F0] bg-white text-[#4A5565] hover:border-[#CBD5E1]"
                                        }`}
                                      >
                                        <Phone size={18} className="shrink-0 text-[#1776F0]" />
                                        <div className="text-left">
                                          <p className="leading-tight font-bold">Phone Call</p>
                                          <span className="text-[10px] font-medium opacity-75">15 Min Session</span>
                                        </div>
                                      </button>

                                      <button
                                        type="button"
                                        onClick={() => handleSessionTypeChange("video")}
                                        className={`flex items-center justify-center gap-2 rounded-xl border p-3.5 text-xs sm:text-sm font-semibold transition cursor-pointer ${
                                          sessionType === "video"
                                            ? "border-[#8F3EBC] bg-[#FAF5FF] text-[#8F3EBC] shadow-sm ring-2 ring-[#8F3EBC]/20"
                                            : "border-[#E2E8F0] bg-white text-[#4A5565] hover:border-[#CBD5E1]"
                                        }`}
                                      >
                                        <Video size={18} className="shrink-0 text-[#8F3EBC]" />
                                        <div className="text-left">
                                          <p className="leading-tight font-bold">Google Meet</p>
                                          <span className="text-[10px] font-medium opacity-75">30 Min Session</span>
                                        </div>
                                      </button>
                                    </div>
                                  </div>

                                  {/* Country Selection */}
                                  <div>
                                    <label className="mb-2 block text-xs sm:text-sm font-semibold text-black">
                                      Interested Countries
                                    </label>
                                    <ChooseCountryDropdown
                                      options={countryOptions}
                                      onChange={handleCountryChange}
                                      placeholder="Select Multiple Countries"
                                      className="!rounded-xl !border-[1.5px] !border-[#E5E7EB] !bg-white !px-4 !py-3 !text-sm !font-medium !text-slate-700"
                                    />
                                    {countryError && (
                                      <p className="mt-1.5 text-xs ff_poppins text-[#EA4335]">
                                        {countryError}
                                      </p>
                                    )}
                                  </div>

                                  {/* Target Intake */}
                                  <div>
                                    <label className="mb-2 block text-xs sm:text-sm font-semibold text-black">
                                      Target Intake
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                      {intakeListOptions.map((item) => (
                                        <button
                                          key={item.id}
                                          type="button"
                                          onClick={() => setSelectedIntake(item.title)}
                                          className={`rounded-xl border px-4 py-2.5 text-xs sm:text-sm font-medium transition cursor-pointer ${
                                            selectedIntake === item.title
                                              ? "border-[#9810FA] bg-[#FAF5FF] text-[#9810FA] font-bold"
                                              : "border-[#E5E7EB] bg-white text-slate-700 hover:border-slate-300"
                                          }`}
                                        >
                                          {item.title} Intake
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Qualification */}
                                  <div>
                                    <label className="mb-2 block text-xs sm:text-sm font-semibold text-black">
                                      Highest / Previous Qualification
                                    </label>
                                    <ChooseCountryDropdown
                                      options={qualificationOptions.map(
                                        (item, index) => ({
                                          id: index + 1,
                                          title: item.value,
                                        }),
                                      )}
                                      onChange={handleQualificationChange}
                                      maxSelection={1}
                                      placeholder="Select Previous Qualification"
                                      className="!rounded-xl !border-[1.5px] !border-[#E5E7EB] !bg-white !px-4 !py-3 !text-sm !font-medium !text-slate-700"
                                      syncFromProfileCountries={false}
                                    />
                                    {qualificationError && (
                                      <p className="mt-1.5 text-xs ff_poppins text-[#EA4335]">
                                        {qualificationError}
                                      </p>
                                    )}
                                  </div>

                                  {/* Sub-step 1 Next Action */}
                                  <div className="pt-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (selectedCountries.length === 0) {
                                          setCountryError("Please select interested country.");
                                          return;
                                        }
                                        setCountryError("");
                                        if (!selectedQualification) {
                                          setQualificationError("Please select previous qualification.");
                                          return;
                                        }
                                        setQualificationError("");
                                        setWizardSubStep(2);
                                      }}
                                      className="flex w-full items-center justify-center gap-2 rounded-xl py-3.5 px-6 gradient_button_shadow bg-button-gradient active:scale-95 text-white font-extrabold text-sm sm:text-base transition hover:opacity-95 cursor-pointer"
                                    >
                                      Proceed to Select Slots →
                                    </button>
                                  </div>
                                </div>
                              ) : wizardSubStep === 2 ? (
                                /* ──── WIZARD SUB-STEP 2: DATE & TIME SLOTS ──── */
                                <div className="space-y-5">
                                  {/* Date picker */}
                                  <div>
                                    <p className="mb-3 text-xs sm:text-sm font-bold text-black">
                                      Select Date
                                    </p>
                                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                      {nonSundaySlots.map(
                                        ({ month, day, fullDate }) => {
                                          const slotDate = new Date(
                                            `${fullDate}T00:00:00`,
                                          );
                                          const todayStr = new Date().toISOString().split("T")[0];
                                          const tomorrowDate = new Date();
                                          tomorrowDate.setDate(tomorrowDate.getDate() + 1);
                                          const tomorrowStr = tomorrowDate.toISOString().split("T")[0];

                                          const isToday = fullDate === todayStr;
                                          const isTomorrow = fullDate === tomorrowStr;

                                          const weekday = isToday
                                            ? "Today"
                                            : isTomorrow
                                              ? "Tomorrow"
                                              : Number.isNaN(slotDate.getTime())
                                                ? ""
                                                : slotDate.toLocaleDateString("en-US", {
                                                    weekday: "short",
                                                  });
                                          const isSelected = selectedDate === fullDate;
                                          return (
                                            <button
                                              key={fullDate}
                                              type="button"
                                              onClick={() =>
                                                handleDateSelection(fullDate)
                                              }
                                              className={`rounded-[12px] border px-2.5 py-3 text-center transition sm:rounded-[14px] sm:px-3 sm:py-3.5 cursor-pointer ${
                                                isSelected
                                                  ? "border-[#9810FA] bg-[#FAF5FF] ring-2 ring-[#9810FA]/20"
                                                  : "border-[#E5E7EB] bg-white hover:border-[#a5b4fc]"
                                              }`}
                                            >
                                              <p className="text-[11px] font-semibold capitalize text-[#4A5565] sm:text-xs">
                                                {weekday}
                                              </p>
                                              <p className="text-base font-bold text-black sm:text-lg my-0.5">
                                                {Number(day)}
                                              </p>
                                              <p className="text-[10px] font-semibold text-[#4A5565] sm:text-[11px]">
                                                {month}
                                              </p>
                                            </button>
                                          );
                                        },
                                      )}
                                    </div>
                                  </div>

                                  {/* Time picker & Mobile Dropdown */}
                                  <div>
                                    <div className="mb-3 flex items-center justify-between">
                                      <p className="text-xs sm:text-sm font-bold text-black">
                                        Select Time Slot ({sessionType === "telephonic" ? "15 Min" : "30 Min"})
                                      </p>
                                    </div>

                                    {/* Mobile-friendly styled select dropdown on small screens */}
                                    <div className="block sm:hidden mb-3">
                                      <div className="relative">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-[#1776F0]">
                                          <Clock3 size={15} />
                                        </div>
                                        <select
                                          value={selectedSlotTime}
                                          onChange={(e) => handleSlotSelection(e.target.value)}
                                          disabled={availableSlotList.length === 0 || isFetchingSlots}
                                          className={`w-full appearance-none rounded-xl border bg-slate-50/80 pl-10 pr-10 py-3 text-xs font-semibold text-slate-800 transition shadow-sm focus:bg-white focus:outline-none focus:ring-2 ${
                                            selectedSlotTime
                                              ? "border-[#1776F0] bg-blue-50/30 ring-2 ring-[#1776F0]/20 text-[#1776F0]"
                                              : "border-slate-300 focus:border-[#1776F0] focus:ring-[#1776F0]/20"
                                          }`}
                                        >
                                          <option value="">
                                            {isFetchingSlots
                                              ? "Loading available slots..."
                                              : availableSlotList.length === 0
                                                ? "No slots available for this date"
                                                : "-- Select a Time Slot --"}
                                          </option>
                                          {availableSlotList.map((item) => (
                                            <option
                                              key={item.startUtc}
                                              value={item.time}
                                              disabled={item.isUnavailable}
                                            >
                                              {item.time} {item.isUnavailable ? "(Booked / Unavailable)" : ""}
                                            </option>
                                          ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3.5 pointer-events-none text-slate-400">
                                          <ChevronDown size={16} />
                                        </div>
                                      </div>
                                    </div>

                                    {/* Slot Button Grid (Tablet & Desktop) */}
                                    {isFetchingSlots ? (
                                      <SlotsSkeleton />
                                    ) : availableSlotList.length > 0 ? (
                                      <div className="hidden sm:grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                                        {availableSlotList.map((item) => {
                                          const isSelected = selectedSlotTime === item.time;
                                          const isUnavailable = item.isUnavailable;
                                          return (
                                            <button
                                              key={item.startUtc}
                                              type="button"
                                              onClick={() =>
                                                !isUnavailable &&
                                                handleSlotSelection(item.time)
                                              }
                                              disabled={isUnavailable}
                                              className={`flex min-h-[52px] sm:min-h-[58px] flex-col items-center justify-center gap-1 rounded-[12px] border px-3 py-2 text-xs sm:text-sm font-semibold transition sm:px-4 cursor-pointer ${
                                                isUnavailable
                                                  ? "bg-rose-50/50 border-rose-200 text-rose-400 line-through cursor-not-allowed"
                                                  : isSelected
                                                    ? "bg-[#1776F0] text-white border-[#1776F0] shadow-md ring-2 ring-[#1776F0]/20"
                                                    : "bg-emerald-50/60 border-emerald-300 text-emerald-800 hover:border-emerald-500 hover:bg-emerald-100/80"
                                              }`}
                                            >
                                              <div className="flex items-center gap-1.5">
                                                <Clock3 size={13} className={isSelected ? "text-white" : isUnavailable ? "text-rose-300" : "text-emerald-600"} />
                                                <span>{item.time}</span>
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <p className="rounded-[14px] border border-dashed border-[#E5E7EB] px-4 py-6 text-center text-sm text-[#4A5565]">
                                        No slots available for the selected date.
                                      </p>
                                    )}
                                  </div>

                                  {/* Sub-step 2 Actions */}
                                  <div className="pt-2 flex items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() => setWizardSubStep(1)}
                                      className="rounded-xl border border-slate-300 bg-white px-5 py-3.5 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer"
                                    >
                                      ← Back
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleProceedFromSubStep2}
                                      disabled={!canConfirmSlot}
                                      className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3.5 px-6 text-xs sm:text-sm font-extrabold transition cursor-pointer ${
                                        canConfirmSlot
                                          ? "gradient_button_shadow bg-button-gradient text-white active:scale-95 hover:opacity-95"
                                          : "cursor-not-allowed bg-slate-200 text-white"
                                      }`}
                                    >
                                      {isConfirmingSlot ? (
                                        <>
                                          <Loader />
                                          Confirming...
                                        </>
                                      ) : user && !user.isAnonymous ? (
                                        "Confirm & Book Slot →"
                                      ) : (
                                        "Continue to Confirm →"
                                      )}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                /* ──── WIZARD SUB-STEP 3: GUEST CONTACT & OTP VERIFICATION ──── */
                                <div className="space-y-4">
                                  <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setWizardSubStep(2);
                                        setIsOtpSent(false);
                                        setOtp("");
                                        setOtpError("");
                                      }}
                                      className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-600 hover:text-black cursor-pointer"
                                    >
                                      ← Back to Slots
                                    </button>
                                    <span className="text-xs font-bold text-[#1776F0] bg-blue-50 px-2.5 py-1 rounded-md border border-blue-100">
                                      🗓️ {selectedDate} at {selectedSlotTime}
                                    </span>
                                  </div>

                                  {!isOtpSent ? (
                                    <form onSubmit={handleSendOtp} className="space-y-3.5">
                                      <div>
                                        <label className="block text-xs sm:text-sm font-semibold text-black mb-1">
                                          Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                          <input
                                            type="text"
                                            value={fullName}
                                            onChange={(e) => {
                                              setFullName(e.target.value);
                                              if (fullNameError) setFullNameError("");
                                            }}
                                            placeholder="Enter your full name"
                                            className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-xs sm:text-sm text-slate-800 font-medium focus:border-[#1776F0] focus:outline-none"
                                          />
                                          <User className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                                        </div>
                                        {fullNameError && (
                                          <p className="mt-1 text-xs text-red-500">{fullNameError}</p>
                                        )}
                                      </div>

                                      <div>
                                        <label className="block text-xs sm:text-sm font-semibold text-black mb-1">
                                          Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                          <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => {
                                              setEmail(e.target.value);
                                              if (emailError) setEmailError("");
                                            }}
                                            placeholder="name@example.com"
                                            className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-xs sm:text-sm text-slate-800 font-medium focus:border-[#1776F0] focus:outline-none"
                                          />
                                          <Mail className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                                        </div>
                                        {emailError && (
                                          <p className="mt-1 text-xs text-red-500">{emailError}</p>
                                        )}
                                      </div>

                                      <div>
                                        <label className="block text-xs sm:text-sm font-semibold text-black mb-1">
                                          Mobile Number (for OTP) <span className="text-red-500">*</span>
                                        </label>
                                        <div className="phone-input-container">
                                          <PhoneInput
                                            defaultCountry="in"
                                            value={phone}
                                            onChange={(val) => {
                                              setPhone(val);
                                              if (phoneError) setPhoneError("");
                                            }}
                                            inputClassName="!w-full !h-11 !rounded-r-xl !border-slate-200 !bg-white !text-xs sm:!text-sm !text-slate-800 focus:!border-[#1776F0]"
                                            className="!w-full"
                                          />
                                        </div>
                                        {phoneError && (
                                          <p className="mt-1 text-xs text-red-500">{phoneError}</p>
                                        )}
                                      </div>

                                      <button
                                        type="submit"
                                        disabled={isSendingOtp}
                                        className="mt-3 w-full h-12 rounded-xl gradient_button_shadow bg-button-gradient text-sm sm:text-base font-extrabold text-white transition hover:opacity-95 disabled:opacity-50 cursor-pointer inline-flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
                                      >
                                        {isSendingOtp ? (
                                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <>
                                            <span>Send Verification OTP</span>
                                            <ArrowRight className="h-4 w-4" />
                                          </>
                                        )}
                                      </button>
                                    </form>
                                  ) : (
                                    <form onSubmit={handleVerifyOtpAndConfirm} className="space-y-4">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="text-xs sm:text-sm font-bold text-slate-900">
                                            Enter 6-digit OTP
                                          </p>
                                          <p className="text-xs text-slate-500">
                                            Sent to <strong className="text-slate-800">{phone}</strong>{" "}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setIsOtpSent(false);
                                                setOtp("");
                                                setOtpError("");
                                              }}
                                              className="text-[#1776F0] underline font-semibold ml-1 cursor-pointer"
                                            >
                                              Edit
                                            </button>
                                          </p>
                                        </div>

                                        <button
                                          type="button"
                                          onClick={handleResendOtp}
                                          disabled={resendCountdown > 0 || isSendingOtp}
                                          className={`text-xs font-semibold ${
                                            resendCountdown > 0
                                              ? "text-slate-400 cursor-not-allowed"
                                              : "text-[#1776F0] hover:underline cursor-pointer"
                                          }`}
                                        >
                                          {resendCountdown > 0 ? `Resend (${resendCountdown}s)` : "Resend"}
                                        </button>
                                      </div>

                                      <div className="flex justify-center py-2">
                                        <InputOTP
                                          required
                                          pattern={REGEXP_ONLY_DIGITS}
                                          maxLength={6}
                                          value={otp}
                                          onChange={(val) => {
                                            setOtp(val);
                                            if (otpError) setOtpError("");
                                          }}
                                        >
                                          <InputOTPGroup className="gap-1.5 sm:gap-2">
                                            <InputOTPSlot index={0} className="!w-10 !h-11 sm:!w-12 sm:!h-12 !rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50 focus:!bg-white focus:!border-[#1776F0]" />
                                            <InputOTPSlot index={1} className="!w-10 !h-11 sm:!w-12 sm:!h-12 !rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50 focus:!bg-white focus:!border-[#1776F0]" />
                                            <InputOTPSlot index={2} className="!w-10 !h-11 sm:!w-12 sm:!h-12 !rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50 focus:!bg-white focus:!border-[#1776F0]" />
                                          </InputOTPGroup>
                                          <InputOTPSeparator className="text-slate-400 font-bold mx-1" />
                                          <InputOTPGroup className="gap-1.5 sm:gap-2">
                                            <InputOTPSlot index={3} className="!w-10 !h-11 sm:!w-12 sm:!h-12 !rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50 focus:!bg-white focus:!border-[#1776F0]" />
                                            <InputOTPSlot index={4} className="!w-10 !h-11 sm:!w-12 sm:!h-12 !rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50 focus:!bg-white focus:!border-[#1776F0]" />
                                            <InputOTPSlot index={5} className="!w-10 !h-11 sm:!w-12 sm:!h-12 !rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50 focus:!bg-white focus:!border-[#1776F0]" />
                                          </InputOTPGroup>
                                        </InputOTP>
                                      </div>

                                      {otpError && (
                                        <p className="text-center text-xs text-rose-500">{otpError}</p>
                                      )}

                                      <button
                                        type="submit"
                                        disabled={isVerifyingOtp || isConfirmingSlot || otp.length < 6}
                                        className="w-full h-12 rounded-xl gradient_button_shadow bg-button-gradient text-sm sm:text-base font-extrabold text-white transition hover:opacity-95 disabled:opacity-50 cursor-pointer inline-flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
                                      >
                                        {isVerifyingOtp || isConfirmingSlot ? (
                                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <>
                                            <span>Verify & Confirm Booking</span>
                                            <ArrowRight className="h-4 w-4" />
                                          </>
                                        )}
                                      </button>
                                    </form>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* ── STEP 3: CONFIRMATION ── */
                        <div className="max-h-[100dvh] overflow-y-auto bg-[linear-gradient(135deg,#FDF2F8_0%,#FFF7ED_50%,#FFFFFF_100%)] px-5 pb-6 pt-14 text-center sm:max-h-[90vh] sm:p-8 ff_poppins">
                          <div className="mx-auto max-w-[560px]">
                            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#dcfce7] sm:h-20 sm:w-20">
                              <BookConfirmCheck />
                            </span>
                            <div className="mt-6">
                              <h3 className="text-xl font-bold text-black sm:text-2xl">
                                Booking Confirmed!
                              </h3>
                              <p className="mt-2 text-sm text-[#4A5565] sm:text-base">
                                Your session has been scheduled successfully.
                              </p>
                            </div>
                            <div className="mt-6 overflow-hidden rounded-[20px] border border-[#eceaf8] bg-[#f7f4ff] text-left">
                              {bookingDetails.map((item) => (
                                <div
                                  key={item.label}
                                  className="flex flex-col items-start gap-1 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4"
                                >
                                  <span className="text-[#4A5565]">
                                    {item.label}:
                                  </span>
                                  <span className="text-sm font-semibold text-black sm:text-right sm:text-base">
                                    {item.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-5 flex items-start gap-3 rounded-[14px] border border-[#bfdbfe] bg-[#eff6ff] px-4 py-3 text-left text-sm text-[#193CB8] sm:items-center sm:text-center">
                              <Mail className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" />
                              <span>
                                A confirmation email has been sent to your
                                registered email address
                              </span>
                            </div>
                            <Link
                              href={"/dashboard/sessions"}
                              className="mt-6 w-full rounded-xl gradient_button_shadow bg-button-gradient py-4 text-base font-semibold text-white transition hover:opacity-95 sm:text-xl cursor-pointer inline-block"
                            >
                              Done
                            </Link>
                          </div>
                        </div>
                      )}
                </div>
              </div>
            </div>
          </div>

          {showVerifyEmail && (
            <div className="fixed left-0 top-0 z-[999] flex h-full w-full items-center justify-center">
              <span
                onClick={() => setShowVerifyEmail(false)}
                className="absolute h-full w-full bg-black/40 z-[99]"
              ></span>
              <EmailVerification
                setEmailVerificationPopup={setShowVerifyEmail}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TalkToExperts;
